import {
  useCallback,
  useEffect,
  useEffectEvent,
  useRef,
  useState,
} from "react";

// Add more Chopin question types here.
//If updating this, make sure to also update the useChopinQuestion mock.
// These values correspond to field names in the Chopin questionnaire, or are aliased to multiple questions (see questionAliases below).
// A question aliased to other questions will cause the hook to return the first non-null answer among those questions.
export type ChopinQuestion =
  | "trade_selector"
  | "business_type"
  | "uk_postcode"
  | "years_trading"
  | "annual_turnover"
  | "what_are_you_here_to_do"
  | "have_employees"
  | "temporary_workers"
  | "use_subcontractors"
  | "town";

// Add question aliases here.
//If you do, make sure to update tests.
const questionAliases: Partial<Record<ChopinQuestion, string[]>> = {
  trade_selector: ["primary_trade", "original_shop_type", "profession"],
};

const extractValueFromForm = (
  form: HTMLFormElement,
  question: ChopinQuestion,
): string | null | undefined => {
  const data = new FormData(form);
  const value = extractValueFromFormData(data, question);

  if (questionAliases[question] !== undefined) {
    const aliasValues = questionAliases[question].map(alias =>
      extractValueFromFormData(data, alias),
    );

    return (
      aliasValues.find(aliasValue => aliasValue) ??
      aliasValues.find(aliasValue => aliasValue !== undefined)
    );
  }

  return value;
};

const extractValueFromFormData = (
  data: FormData,
  fieldName: string,
): string | null | undefined => {
  const answer = data.get(fieldName);

  if (!isFieldVisible(fieldName)) return undefined;
  if (!answer || answer === "NaN") return null;

  return answer as string;
};

const isFieldVisible = (fieldName: string): boolean => {
  return Array.from(document.getElementsByName(fieldName)).some(
    (el: HTMLElement) => {
      const controlBlock = el.closest(".control-block");

      return controlBlock?.classList.contains("on") ?? false;
    },
  );
};

// Hook for using Chopin question answers, if the question is not answered, it will return null. If the question
// is not showing on the page, it will return undefined. If it is answered, it will return the answer as a string.
export const useChopinQuestion = (question: ChopinQuestion) => {
  const [value, setValue] = useState<string | null | undefined>(undefined);

  // Helper to find all Chopin forms in the document
  const getChopinForms = useCallback(() => {
    return Array.from(
      document.querySelectorAll<HTMLFormElement>("form"),
    ).filter(form => form.action.includes("/q/"));
  }, []);

  // useEffectEvent ensures the handler always reads the latest question value
  // without causing the effect to re-run when the handler reference changes
  const handleFormChange = useEffectEvent((form: HTMLFormElement) => {
    setValue(extractValueFromForm(form, question));
  });

  // Attach change listeners to Chopin forms with proper cleanup
  useEffect(() => {
    const forms = getChopinForms();
    const handlers = new Map<HTMLFormElement, (e: Event) => void>();

    forms.forEach(form => {
      const handler = () => handleFormChange(form);
      handlers.set(form, handler);
      form.addEventListener("change", handler);
    });

    return () => {
      handlers.forEach((handler, form) => {
        form.removeEventListener("change", handler);
      });
    };
  }, [getChopinForms]);

  // On first render, extract the current value from the form
  // This is done outside of a useEffect so that we don't initially return null
  // when the hook is first called if the field already has a value
  const loadedInitialValues = useRef(false);
  const prevQuestionRef = useRef(question);

  if (!loadedInitialValues.current || prevQuestionRef.current !== question) {
    loadedInitialValues.current = true;
    prevQuestionRef.current = question;

    getChopinForms().forEach(form => {
      setValue(extractValueFromForm(form, question));
    });
  }

  return {
    answer: value,
  };
};
